<script setup>
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { quickstartOpened } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import UserImage from "@/components/UserImage.vue";
import { useAppStore } from "@/stores";

import BaseDropdown from "./BaseDropdown.vue";
import SvgIcon from "./SvgIcon.vue";
import { useQuickStart } from "./quickStart/useQuickStart";

const { user } = storeToRefs(useAppStore());

const userDisplayName = computed(() => user.value?.name || user.value?.email || "");

const {
  canSeeQuickStart,
  buttonDismissed: quickStartButtonDismissed,
  setSidebarOpen: setQuickStartOpen,
  setButtonDismissed: setQuickStartButtonDismissed,
} = useQuickStart();

function restoreQuickStart() {
  setQuickStartButtonDismissed(false);
  setQuickStartOpen(true);
  trackEvent(quickstartOpened("user_menu"));
}
</script>

<template>
  <BaseDropdown align="left" class="user-menu">
    <template #trigger>
      <div class="user-row">
        <UserImage :user size="medium" />
        <span class="user-row-name">{{ userDisplayName }}</span>
        <SvgIcon
          name="system/chevron-up-down"
          width="18"
          height="18"
          class="chevron"
          aria-hidden="true"
        />
      </div>
    </template>

    <div v-if="user.id" class="user-info" role="presentation">
      <UserImage :user size="big" />
      <div class="user-info-text">
        <p v-if="user.name" class="user-name">{{ user.name }}</p>
        <p class="user-email">{{ user.email }}</p>
      </div>
    </div>

    <hr class="divider-line" role="presentation" />

    <router-link
      v-if="user.id"
      :to="{ name: 'profile' }"
      class="menu-item list-item"
      role="menuitem"
    >
      <SvgIcon name="persona/user" width="18" height="18" />
      Profile
    </router-link>

    <router-link :to="{ name: 'help' }" class="menu-item list-item" role="menuitem">
      <SvgIcon name="system/help" width="18" height="18" />
      Help
    </router-link>

    <button
      v-if="canSeeQuickStart && quickStartButtonDismissed"
      type="button"
      class="menu-item list-item"
      role="menuitem"
      @click="restoreQuickStart"
    >
      <SvgIcon name="system/quickstart" width="18" height="18" />
      Quickstart
    </button>

    <hr class="divider-line" role="presentation" />

    <router-link :to="{ name: 'logout' }" class="menu-item list-item" role="menuitem">
      <SvgIcon name="general/logout" width="18" height="18" />
      Log Out
    </router-link>
  </BaseDropdown>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";
@use "@/styles/palette" as p;

.user-menu {
  // Dropdown trigger should be full width of sidebar
  > :deep(.dropdown-trigger) {
    width: 100%;
  }

  .user-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    background: none;
    text-align: left;

    &:hover {
      background-color: p.$black-alpha-5;
    }

    .user-row-name {
      @include typography.medium;

      color: p.$brand-70;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    .chevron {
      margin-left: auto;
      flex-shrink: 0;
      color: c.$text-subtle;
    }
  }
}

// Dropdown content lives below — BaseDropdown teleports it to <body>, but
// scoped data-v attributes still travel with the rendered nodes, so these
// selectors continue to match.

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;

  .user-info-text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    min-width: 0;

    .user-name {
      @include typography.medium("semi-bold");

      color: c.$text;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .user-email {
      @include typography.small("regular");

      color: c.$text-subtle;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.divider-line {
  border: none;
  border-top: 1px solid c.$border-subtle;

  // Span the full card width: the dropdown content has 4px padding, so the
  // negative margins bleed to both edges. width must grow to match, otherwise
  // the block's auto width stays inset and only the left margin takes effect
  // (line shifts left, falls short on the right).
  width: calc(100% + 8px);
  margin: 4px -4px;
}

.menu-item {
  @include typography.medium;

  gap: 8px;
  text-decoration: none;

  // Reset native <button> styles (Quickstart action item) so it matches the
  // router-link rows.
  border: none;
  background: none;
  font-family: inherit;
  text-align: left;
}
</style>
